import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useButton, useI18n } from '~Hooks';
import { handyBreakpoint } from '~Styles/breakpoints';
import { Box, GreenButton, Input, RedButton, Textarea } from '~Styles';
import { insideWrapperStyles } from '~ComponentsImproved/Modal';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ErrorBox = styled(Box)`
  background-color: color-mix(in srgb, var(--colorUIRed) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--colorUIRed) 30%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
  animation: ${fadeIn} 0.2s ease-in-out forwards;
`;

const Wrapper = styled(Box)<{ width: string }>`
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  width: ${({ width }) => width};
  margin-bottom: 25%;
  box-sizing: border-box;
  max-width: 500px;

  ${handyBreakpoint} {
    margin-bottom: auto;
    margin-top: 16px;
  }
`;

const ButtonWrapper = styled(Box)`
  flex-flow: row nowrap;
`;

interface Props {
  type?: string;
  /**
   * Forwarded to the single-line input (`type="text"`) so callers can ask for a
   * numeric keypad on touch devices. Left unset for every other caller, which
   * keeps the rendered input unchanged.
   */
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
  onSubmit: (message: string) => void;
  onInput?: (message: string) => void;
  value?: string;
  placeHolder?: string;
  autoFocus?: boolean;
  width?: string;
  acceptEmpty?: boolean;
  error?: string;
}

const MessageInput: React.FunctionComponent<Props> = ({
  type,
  inputMode,
  onSubmit,
  onInput,
  value = '',
  placeHolder = '',
  autoFocus = true,
  width = '100%',
  acceptEmpty = false,
  error = '',
}) => {
  const [message, setMessage] = useState(value);
  const { i18n } = useI18n();

  useEffect(() => {
    setMessage(value);
  }, [value]);

  const handleClear = () => {
    if (!message.length) return;

    setMessage('');
    onInput && onInput('');
  };

  const buttonDisabled = acceptEmpty ? !acceptEmpty : !message.length;

  const clearButton = useButton({
    onClick: handleClear,
  });

  const saveButton = useButton({
    onClick: () => {
      onSubmit(message);
    },
    disabled: buttonDisabled,
  });

  return (
    <Wrapper
      display="flex"
      flexDirection="column"
      gap="xs"
      padding="small"
      width={width}
      onClick={(e) => {
        e.stopPropagation();
      }}>
      {type === 'text' ? (
        <Input
          dark
          type="text"
          inputMode={inputMode}
          autoFocus={autoFocus}
          value={message}
          placeholder={placeHolder}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setMessage(event.target.value);
            onInput && onInput(event.target.value);
          }}
        />
      ) : (
        <Textarea
          dark
          autoFocus={autoFocus}
          value={message}
          placeholder={placeHolder}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setMessage(event.target.value);
            onInput && onInput(event.target.value);
          }}
        />
      )}

      {error && (
        <ErrorBox display="flex" alignItems="center" gap="xs">
          <Paragraph level={5} color="var(--colorUIRed)">
            {error}
          </Paragraph>
        </ErrorBox>
      )}

      <ButtonWrapper display="flex" gap="xs" justifyContent="space-between">
        <RedButton {...clearButton} disabled={!message.length}>
          {i18n('General.Clear')}
        </RedButton>

        <GreenButton
          {...saveButton}
          style={{
            flex: 2,
          }}>
          {i18n('General.Ok')}
        </GreenButton>
      </ButtonWrapper>
    </Wrapper>
  );
};

export default MessageInput;
